// pages/features/tickets/TicketDetailsPage.tsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Phone,
  Calendar,
  FileText,
  Send,
  Eye,
  EyeOff,
  Edit2,
  Save,
  X,
  ArrowLeft,
  Paperclip,
  Download,
  File,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RotateCcw,
  BookOpen,
  Copy,
  Tag,
  MessageSquare,
  Activity,
  Inbox,
  UserCircle,
  Mail,
  PhoneCall,
  Globe,
  LayoutDashboard,
  HelpCircle,
} from "lucide-react";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { Ticket } from "../../../types/features/Ticket";
import useAuth from "../../../hooks/features/auth/useAuth";
import { TicketMessage } from "../../../types/features/TicketMessage";
import { ticketMessageService } from "../../../services/ticketMessageService";
import { ticketService } from "../../../services/ticketService";
import useCategories from "../../../hooks/features/vitrine/categories/useCategories";

export default function TicketDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(true);

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editSolution, setEditSolution] = useState("");
  const [editCategories, setEditCategories] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const { categories } = useCategories();

  const problemCategoriesList = categories.map((c) => ({
    name: c.name,
    slug: c.slug || "",
  }));

  useEffect(() => {
    if (id) {
      loadTicket();
      loadMessages();
    }
  }, [id]);

  const loadTicket = async () => {
    setLoading(true);
    try {
      const data = await ticketService.getTicketById(id!);
      setTicket(data);
      setEditTitle(data?.title || "");
      setEditDescription(data?.description || "");
      setEditSolution(data?.solution || "");
      setEditCategories(data?.problem_categories || []);
    } catch (error) {
      console.error("Erreur lors du chargement du ticket:", error);
      alertError("Erreur lors du chargement du ticket");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    if (!id) return;
    try {
      const data = await ticketMessageService.getMessages(id);
      setMessages(data);
    } catch (error) {
      console.error("Erreur lors du chargement des messages:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !ticket || !currentUser) return;

    setSending(true);
    try {
      const messageData = {
        ticket_id: ticket.id,
        author_type: "agent" as const,
        author_id: currentUser.id,
        message: newMessage.trim(),
        is_internal: isInternalNote,
      };

      const sentMessage = await ticketMessageService.sendMessage(messageData);
      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage("");
      setIsInternalNote(false);

      alertSuccess(isInternalNote ? "Note interne ajoutée" : "Message envoyé");
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      alertError("Erreur lors de l'envoi du message");
    } finally {
      setSending(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!ticket) return;

    setSaving(true);
    try {
      await ticketService.updateTicket(ticket.id, {
        title: editTitle,
        description: editDescription,
        solution: editSolution,
        problem_categories: editCategories,
      });

      alertSuccess("Modifications enregistrées");
      setIsEditing(false);

      setTicket({
        ...ticket,
        title: editTitle,
        description: editDescription,
        solution: editSolution,
        problem_categories: editCategories,
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      alertError("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (status: Ticket["status"]) => {
    if (!ticket) return;
    try {
      await ticketService.updateTicket(ticket.id, { status });
      setTicket({ ...ticket, status });
      alertSuccess(`Statut mis à jour: ${getStatusLabel(status)}`);
    } catch (error) {
      alertError("Erreur lors du changement de statut");
    }
  };

  const toggleCategory = (slug: string) => {
    setEditCategories((prev) =>
      prev.includes(slug) ? prev.filter((c) => c !== slug) : [...prev, slug]
    );
  };

  const getPriorityLabel = () => {
    switch (ticket?.priority) {
      case "critique":
        return { label: "Critique", className: "bg-red-100 text-red-700" };
      case "haute":
        return { label: "Haute", className: "bg-orange-100 text-orange-700" };
      case "moyenne":
        return { label: "Moyenne", className: "bg-yellow-100 text-yellow-700" };
      case "basse":
        return { label: "Basse", className: "bg-green-100 text-green-700" };
      default:
        return { label: "Moyenne", className: "bg-gray-100 text-gray-700" };
    }
  };

  const getStatusLabel = (status?: Ticket["status"]) => {
    const s = status || ticket?.status;
    switch (s) {
      case "nouveau":
        return "Nouveau";
      case "en_cours":
        return "En cours";
      case "en_attente":
        return "En attente";
      case "escalade":
        return "Escalade";
      case "resolu":
        return "Résolu";
      case "ferme":
        return "Fermé";
      default:
        return "Nouveau";
    }
  };

  const getStatusClassName = (status?: Ticket["status"]) => {
    const s = status || ticket?.status;
    switch (s) {
      case "nouveau":
        return "bg-blue-100 text-blue-700";
      case "en_cours":
        return "bg-purple-100 text-purple-700";
      case "en_attente":
        return "bg-yellow-100 text-yellow-700";
      case "escalade":
        return "bg-red-100 text-red-700";
      case "resolu":
        return "bg-green-100 text-green-700";
      case "ferme":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusIcon = (status?: Ticket["status"]) => {
    const s = status || ticket?.status;
    switch (s) {
      case "nouveau":
        return Inbox;
      case "en_cours":
        return Activity;
      case "en_attente":
        return Clock;
      case "escalade":
        return AlertTriangle;
      case "resolu":
        return CheckCircle;
      case "ferme":
        return XCircle;
      default:
        return HelpCircle;
    }
  };

  const priority = getPriorityLabel();
  const StatusIcon = getStatusIcon();

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSourceLabel = () => {
    switch (ticket?.source) {
      case "appel":
        return "Appel téléphonique";
      case "formulaire_web":
        return "Formulaire web";
      case "email":
        return "Email";
      case "dashboard":
        return "Tableau de bord";
      default:
        return ticket?.source || "Inconnu";
    }
  };

  const getSourceIcon = () => {
    switch (ticket?.source) {
      case "appel":
        return PhoneCall;
      case "formulaire_web":
        return Globe;
      case "email":
        return Mail;
      case "dashboard":
        return LayoutDashboard;
      default:
        return HelpCircle;
    }
  };

  const SourceIcon = getSourceIcon();

  const getCategoryName = (slug: string) => {
    const cat = problemCategoriesList.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  const visibleMessages = messages.filter((msg) => showInternalNotes || !msg.is_internal);

  const isClosed = ticket?.status === "ferme" || ticket?.status === "resolu";
  const canSendMessage = !isClosed && currentUser;

  const attachments = ticket?.attachments || [];

  const openAttachment = (url: string) => {
    window.open(url, "_blank");
  };

  const isImage = (url: string) => {
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp", ".svg"];
    return imageExtensions.some((ext) => url.toLowerCase().endsWith(ext));
  };

  const getFileName = (url: string) => {
    return url.split("/").pop() || "Fichier";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500">Ticket non trouvé</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-green-600 hover:underline">
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Retour</span>
            </button>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${getStatusClassName()}`}>
                <StatusIcon size={14} />
                {getStatusLabel()}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${priority.className}`}>
                {priority.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Copy size={12} />
                    Référence: {ticket.ticket_number || "N/A"}
                  </span>
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <SourceIcon size={12} />
                    Source: {getSourceLabel()}
                  </span>
                </div>
                {!isEditing && !isClosed && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier le ticket"
                  >
                    <Edit2 size={18} />
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      rows={5}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégories</label>
                    <div className="flex flex-wrap gap-2">
                      {problemCategoriesList.map((cat) => (
                        <button
                          key={cat.slug}
                          type="button"
                          onClick={() => toggleCategory(cat.slug)}
                          className={`px-3 py-1 text-sm rounded-full transition-colors ${
                            editCategories.includes(cat.slug)
                              ? "bg-green-600 text-white"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                          }`}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Solution</label>
                    <textarea
                      value={editSolution}
                      onChange={(e) => setEditSolution(e.target.value)}
                      rows={3}
                      placeholder="Solution apportée au problème..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        setEditTitle(ticket.title || "");
                        setEditDescription(ticket.description || "");
                        setEditSolution(ticket.solution || "");
                        setEditCategories(ticket.problem_categories || []);
                      }}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-1"
                    >
                      <X size={16} />
                      Annuler
                    </button>
                    <button
                      onClick={handleSaveChanges}
                      disabled={saving}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                    >
                      <Save size={16} />
                      {saving ? "Enregistrement..." : "Enregistrer"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">{ticket.title}</h2>
                  <div className="bg-gray-50 rounded-lg p-4 mb-3">
                    <p className="text-gray-600 whitespace-pre-wrap">{ticket.description}</p>
                  </div>

                  {ticket.problem_categories && ticket.problem_categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {ticket.problem_categories.map((cat, idx) => (
                        <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                          <Tag size={10} />
                          {getCategoryName(cat)}
                        </span>
                      ))}
                    </div>
                  )}

                  {ticket.solution && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-800 mb-1 flex items-center gap-1">
                        <CheckCircle size={14} />
                        Solution apportée
                      </h4>
                      <p className="text-sm text-green-700 whitespace-pre-wrap">{ticket.solution}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {attachments.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Paperclip size={16} />
                  Pièces jointes ({attachments.length})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {attachments.map((url, index) => (
                    <div
                      key={index}
                      className="relative group cursor-pointer"
                      onClick={() => openAttachment(url)}
                    >
                      {isImage(url) ? (
                        <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={url}
                            alt={`Pièce jointe ${index + 1}`}
                            className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                          />
                        </div>
                      ) : (
                        <div className="aspect-square bg-gray-100 rounded-lg border border-gray-200 flex flex-col items-center justify-center p-4 hover:bg-gray-50 transition-colors">
                          <File size={32} className="text-gray-400 mb-2" />
                          <p className="text-xs text-gray-500 text-center truncate w-full">
                            {getFileName(url)}
                          </p>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                        <Download size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <MessageSquare size={16} />
                  Conversation
                </h4>
                <button
                  onClick={() => setShowInternalNotes(!showInternalNotes)}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  {showInternalNotes ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showInternalNotes ? "Masquer" : "Afficher"} notes internes
                </button>
              </div>

              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Aucun message</div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {visibleMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-3 rounded-lg ${
                        msg.is_internal
                          ? "bg-yellow-50 border border-yellow-200"
                          : msg.author_type === "client"
                          ? "bg-blue-50 border border-blue-200"
                          : "bg-white border border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                          {msg.author_type === "client" ? (
                            <>
                              <UserCircle size={12} />
                              Client
                            </>
                          ) : msg.is_internal ? (
                            <>
                              <BookOpen size={12} />
                              Note interne - Agent
                            </>
                          ) : (
                            <>
                              <UserCircle size={12} />
                              Agent
                            </>
                          )}
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {canSendMessage && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-2">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrire un message..."
                      rows={2}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 self-end flex items-center gap-1"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="internalNote"
                      checked={isInternalNote}
                      onChange={(e) => setIsInternalNote(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="internalNote" className="text-sm text-gray-600 flex items-center gap-1">
                      <BookOpen size={12} />
                      Note interne (visible seulement par les agents)
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <User size={16} />
                Client
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User size={14} className="text-gray-400" />
                  <span className="text-gray-600">{ticket?.client?.full_name || "Client inconnu"}</span>
                </div>
                {ticket.client?.phone && ticket.client.phone_numbers && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-gray-400" />
                    <span className="text-gray-600">{ticket.client?.phone || ticket.client.phone_numbers}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar size={14} className="text-gray-400" />
                  <span className="text-gray-600">Créé le {formatDate(ticket.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <FileText size={16} />
                Informations
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Copy size={12} />
                    Référence
                  </span>
                  <span className="text-gray-700 font-mono text-xs">{ticket.ticket_number || "N/A"}</span>
                </div>
                {ticket.resolved_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500 flex items-center gap-1">
                      <CheckCircle size={12} />
                      Résolu le
                    </span>
                    <span className="text-gray-700">{formatDate(ticket.resolved_at)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-1">
                    <Clock size={12} />
                    Position
                  </span>
                  <span className="text-gray-700">{ticket.position || "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-1">
                    <SourceIcon size={12} />
                    Source
                  </span>
                  <span className="text-gray-700">{getSourceLabel()}</span>
                </div>
              </div>
            </div>

            {!isClosed && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <RotateCcw size={16} />
                  Changer le statut
                </h4>
                <div className="flex flex-col gap-2">
                  {ticket.status !== "en_cours" && (
                    <button
                      onClick={() => handleStatusChange("en_cours")}
                      className="w-full px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-left flex items-center gap-2"
                    >
                      <Activity size={16} />
                      Prendre en charge
                    </button>
                  )}
                  {ticket.status !== "en_attente" && (
                    <button
                      onClick={() => handleStatusChange("en_attente")}
                      className="w-full px-4 py-2 text-sm bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-left flex items-center gap-2"
                    >
                      <Clock size={16} />
                      Mettre en attente
                    </button>
                  )}
                  {ticket.status !== "escalade" && (
                    <button
                      onClick={() => handleStatusChange("escalade")}
                      className="w-full px-4 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-left flex items-center gap-2"
                    >
                      <AlertTriangle size={16} />
                      Escalader
                    </button>
                  )}
                  <button
                    onClick={() => handleStatusChange("resolu")}
                    className="w-full px-4 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-left flex items-center gap-2"
                  >
                    <CheckCircle size={16} />
                    Marquer comme résolu
                  </button>
                  <button
                    onClick={() => handleStatusChange("ferme")}
                    className="w-full px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-left flex items-center gap-2"
                  >
                    <XCircle size={16} />
                    Fermer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}